import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="container-site">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Explore Mulebuy Spreadsheet{" "}
              <span className="text-accent-600">Categories</span>
            </h2>
            <p className="mt-2 text-foreground-600 text-sm md:text-base">
              Browse all product categories from the updated Mulebuy Spreadsheet
            </p>
          </div>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 bg-background-100 border border-background-300 text-foreground-800 px-5 py-3 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200"
            >
              <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
              All Categories
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-background-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => {
                      handleExternalLink(cat.url);
                      setDropdownOpen(false);
                    }}
                    className="whitespace-nowrap cursor-pointer w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 first:rounded-t-xl last:rounded-b-xl flex items-center justify-between"
                  >
                    {cat.name}
                    <i className="ri-external-link-line w-3.5 h-3.5 flex items-center justify-center text-foreground-400"></i>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleExternalLink(cat.url)}
              className="cursor-pointer group relative bg-white border border-background-200 rounded-xl overflow-hidden hover:border-accent-300 transition-all duration-300"
            >
              <div className="aspect-square overflow-hidden relative">
                <img
                  src={cat.imageUrl}
                  alt={`Mulebuy Spreadsheet ${cat.name} category`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-2.5 md:p-3 pt-8">
                  <p className="text-xs md:text-sm font-semibold text-white text-center whitespace-nowrap overflow-hidden text-ellipsis leading-tight">
                    {cat.name}
                  </p>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <span className="w-6 h-6 flex items-center justify-center bg-white/90 rounded-full">
                    <i className="ri-external-link-line text-foreground-700 text-xs"></i>
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}